import {
  ArrowRight,
  Check,
  CheckCircle2,
  CircleDot,
  Send,
  Sparkles,
  Vote,
} from "lucide-react";
import type { CSSProperties } from "react";
import { hostParticipant } from "../../data/simulatedParticipants";
import {
  getOptionCounts,
  getVoteCounts,
} from "../../game-engine/simulation";
import type {
  QuizRoundDefinition,
  QuizRoundResult,
  RoundDefinition,
  RoundResult,
  SimulatedParticipant,
  VoteRoundResult,
} from "../../game-engine/types";
import type { GameSessionPhase } from "../../hooks/useGameSession";
import { ParticipantAvatar } from "../ParticipantAvatar/ParticipantAvatar";
import styles from "./GameRound.module.css";

interface GameRoundProps {
  round: RoundDefinition;
  result?: RoundResult;
  phase: Extract<GameSessionPhase, "question" | "answering" | "reveal">;
  participants: readonly SimulatedParticipant[];
  answeredParticipantIds: readonly string[];
  hostAnswerId: string | null;
  roundIndex: number;
  roundCount: number;
  onHostAnswer: (answerId: string) => void;
  onNext: () => void;
}

function QuizOptions({
  round,
  result,
  answerId,
  onAnswer,
}: {
  round: QuizRoundDefinition;
  result?: QuizRoundResult;
  answerId: string | null;
  onAnswer: (answerId: string) => void;
}) {
  const counts = result ? getOptionCounts(round, result) : null;

  return (
    <ol className={styles.optionList}>
      {round.options.map((option, index) => {
        const isCorrect = option.id === round.correctOptionId;
        const answerCount = counts?.get(option.id) ?? 0;

        const optionContent = (
          <>
            <span className={styles.optionIndex}>
              {String.fromCharCode(65 + index)}
            </span>
            <span className={styles.optionLabel}>{option.label}</span>
            {result ? (
              <span className={styles.optionResult}>
                {isCorrect ? (
                  <span>
                    <Check aria-hidden="true" />
                    Rätt svar
                  </span>
                ) : null}
                <strong>{answerCount} svar</strong>
              </span>
            ) : null}
          </>
        );

        return (
          <li key={option.id}>
            {result ? (
              <div
                className={styles.option}
                data-correct={isCorrect ? "true" : undefined}
              >
                {optionContent}
              </div>
            ) : (
              <button
                className={styles.option}
                type="button"
                aria-label={`Svara ${String.fromCharCode(65 + index)}: ${option.label}`}
                aria-pressed={answerId === option.id}
                aria-disabled={answerId !== null}
                disabled={answerId !== null && answerId !== option.id}
                data-answerable="true"
                data-selected={answerId === option.id ? "true" : undefined}
                onClick={() => {
                  if (answerId === null) {
                    onAnswer(option.id);
                  }
                }}
              >
                {optionContent}
              </button>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function VoteReveal({
  participants,
  result,
}: {
  participants: readonly SimulatedParticipant[];
  result: VoteRoundResult;
}) {
  const voteCounts = getVoteCounts(participants, result);
  const maxVotes = Math.max(...voteCounts.map((entry) => entry.votes), 1);

  return (
    <ol className={styles.voteResults}>
      {voteCounts.map((entry, index) => (
        <li key={entry.participant.id}>
          <ParticipantAvatar
            participant={entry.participant}
            size="small"
            showName={false}
          />
          <span className={styles.voteName}>{entry.participant.name}</span>
          <span className={styles.voteTrack} aria-hidden="true">
            <span
              style={
                {
                  "--vote-width": `${(entry.votes / maxVotes) * 100}%`,
                } as CSSProperties
              }
            />
          </span>
          <strong>
            {entry.votes} {entry.votes === 1 ? "röst" : "röster"}
          </strong>
          {index === 0 ? (
            <span className={styles.voteWinner}>Gruppens val</span>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function RoundPoints({
  participants,
  result,
}: {
  participants: readonly SimulatedParticipant[];
  result: QuizRoundResult;
}) {
  const entries = result.answers
    .filter((answer) => answer.awardedPoints > 0)
    .sort((first, second) => second.awardedPoints - first.awardedPoints)
    .slice(0, 3)
    .map((answer) => ({
      answer,
      participant: participants.find(
        (candidate) => candidate.id === answer.participantId,
      ),
    }))
    .filter(
      (
        entry,
      ): entry is {
        answer: QuizRoundResult["answers"][number];
        participant: SimulatedParticipant;
      } => entry.participant !== undefined,
    );

  return (
    <div className={styles.roundPoints}>
      <span className={styles.pointsLabel}>Snabbast med rätt svar</span>
      <ol>
        {entries.map(({ answer, participant }) => (
          <li key={participant.id}>
            <ParticipantAvatar
              participant={participant}
              size="small"
              showName={false}
            />
            <span>{participant.name}</span>
            <strong>+{answer.awardedPoints}</strong>
          </li>
        ))}
      </ol>
    </div>
  );
}

function HostVoteControl({
  participants,
  answerId,
  onAnswer,
}: {
  participants: readonly SimulatedParticipant[];
  answerId: string | null;
  onAnswer: (answerId: string) => void;
}) {
  return (
    <fieldset className={styles.hostAnswer}>
      <legend>Ditt svar som spelvärd</legend>
      <p>
        {answerId
          ? "Svaret är låst för den här rundan."
          : "Vem i gruppen passar bäst?"}
      </p>

      <div className={styles.hostVoteOptions}>
        {participants
          .filter(
            (participant) => participant.id !== hostParticipant.id,
          )
          .map((participant) => (
            <button
              type="button"
              key={participant.id}
              aria-label={`Rösta på ${participant.name}`}
              aria-pressed={answerId === participant.id}
              aria-disabled={answerId !== null}
              disabled={
                answerId !== null && answerId !== participant.id
              }
              data-selected={
                answerId === participant.id ? "true" : undefined
              }
              onClick={() => {
                if (answerId === null) {
                  onAnswer(participant.id);
                }
              }}
            >
              <ParticipantAvatar
                participant={participant}
                size="small"
                showName={false}
              />
              {participant.name}
            </button>
          ))}
      </div>
    </fieldset>
  );
}

export function GameRound({
  round,
  result,
  phase,
  participants,
  answeredParticipantIds,
  hostAnswerId,
  roundIndex,
  roundCount,
  onHostAnswer,
  onNext,
}: GameRoundProps) {
  const isReveal = phase === "reveal";
  const isAnswering = phase === "answering";
  const isLastRound = roundIndex === roundCount - 1;
  const answeredCount = answeredParticipantIds.length;
  const everyoneAnswered = answeredCount === participants.length;
  const remainingAnswerCount = participants.length - answeredCount;
  const responseProgressPercentage =
    participants.length > 0
      ? Math.round((answeredCount / participants.length) * 100)
      : 0;
  const responseProgressStage =
    answeredCount === 0
      ? "idle"
      : everyoneAnswered
        ? "complete"
        : responseProgressPercentage >= 65
          ? "near"
          : "active";

  return (
    <main className={styles.round}>
      <section className={styles.questionPanel}>
        <div className={styles.roundMeta}>
          <span>
            Runda {roundIndex + 1} av {roundCount}
          </span>
          <span className={styles.roundType}>
            {round.type === "quiz" ? (
              <CircleDot aria-hidden="true" />
            ) : (
              <Vote aria-hidden="true" />
            )}
            {round.type === "quiz" ? "Quiz" : "Omröstning"}
          </span>
        </div>

        <h1
          data-session-heading={!isReveal ? true : undefined}
          tabIndex={!isReveal ? -1 : undefined}
        >
          {round.prompt}
        </h1>

        {round.type === "quiz" ? (
          <QuizOptions
            round={round}
            result={
              result?.type === "quiz" && isReveal ? result : undefined
            }
            answerId={hostAnswerId}
            onAnswer={onHostAnswer}
          />
        ) : isReveal && result?.type === "vote" ? (
          <VoteReveal participants={participants} result={result} />
        ) : (
          <div className={styles.votePrompt}>
            <Vote aria-hidden="true" />
            <p>
              Alla röstar anonymt på en person i gruppen. Resultatet visas
              automatiskt när hela gruppen har svarat.
            </p>
          </div>
        )}
      </section>

      <aside className={styles.hostPanel} aria-label="Värdkontroller">
        {!isReveal ? (
          <>
            <div className={styles.responseHeader}>
              <div>
                <span className={styles.responseEyebrow}>Svar live</span>
                <h2>
                  {isAnswering
                    ? `${answeredCount} av ${participants.length} klara`
                    : "Redo att svara"}
                </h2>
              </div>
              {everyoneAnswered && isAnswering ? (
                <CheckCircle2 aria-label="Alla har svarat" />
              ) : (
                <Send aria-hidden="true" />
              )}
            </div>

            <div
              className={styles.responseProgress}
              data-stage={responseProgressStage}
              style={
                {
                  "--response-progress": `${responseProgressPercentage}%`,
                } as CSSProperties
              }
            >
              <progress
                className={styles.accessibleProgress}
                max={participants.length}
                value={answeredCount}
                aria-label={`${answeredCount} av ${participants.length} deltagare har svarat`}
              />
              <div className={styles.progressTrack} aria-hidden="true">
                <span className={styles.progressFill}>
                  <span className={styles.progressShimmer} />
                  <span className={styles.progressOrb} />
                </span>
              </div>
              <div className={styles.progressMeta} aria-hidden="true">
                <span>
                  <strong>{answeredCount}</strong> svar inne
                </span>
                <span>
                  {everyoneAnswered
                    ? "Alla klara!"
                    : `${remainingAnswerCount} kvar`}
                </span>
              </div>
            </div>

            <ul className={styles.responseAvatars}>
              {participants.map((participant) => (
                <li key={participant.id}>
                  <ParticipantAvatar
                    participant={participant}
                    size="small"
                    answered={answeredParticipantIds.includes(participant.id)}
                  />
                </li>
              ))}
            </ul>

            {round.type === "vote" ? (
              <HostVoteControl
                participants={participants}
                answerId={hostAnswerId}
                onAnswer={onHostAnswer}
              />
            ) : null}

            <div
              className={styles.autoRevealStatus}
              data-complete={everyoneAnswered ? "true" : undefined}
              aria-live="polite"
            >
              {everyoneAnswered ? (
                <>
                  <Sparkles aria-hidden="true" />
                  Alla är klara – resultaten visas…
                </>
              ) : hostAnswerId ? (
                <>
                  <span className={styles.statusPulse} aria-hidden="true" />
                  Ditt svar är inne. Väntar på resten av gruppen.
                </>
              ) : round.type === "quiz" ? (
                "Välj ett svar i frågekortet för att starta rundan."
              ) : (
                "Välj vem du vill rösta på för att starta rundan."
              )}
            </div>
          </>
        ) : (
          <>
            <div className={styles.revealHeading}>
              <span className={styles.responseEyebrow}>Rundan är klar</span>
              <h2 data-session-heading tabIndex={-1}>
                Så svarade gruppen
              </h2>
            </div>

            {result?.type === "quiz" ? (
              <RoundPoints participants={participants} result={result} />
            ) : (
              <p className={styles.revealCopy}>
                Rösterna är räknade och kvällens gruppfavorit är utsedd.
              </p>
            )}

            <button
              className={styles.primaryAction}
              type="button"
              onClick={onNext}
            >
              {isLastRound ? "Visa slutresultat" : "Nästa fråga"}
              <ArrowRight aria-hidden="true" />
            </button>
          </>
        )}
      </aside>
    </main>
  );
}
